import React from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@/shared/auth';
import {
  isAuthDisconnected,
  notifyAuthDisconnected,
  subscribeAuthDisconnected,
} from '@/lib/auth/authDisconnect';
import { subscribeAuthEvents } from '@/shared/auth/authEvents';
import { setAuthKickoutState } from '@/shared/auth/authKickout';
import { postAuthEvent } from '@/shared/auth/authEvents';

type AuthInlineMessage = {
  title: string;
  description: string;
};

function isLoginPath(pathname: string) {
  return pathname === '/login' || pathname.startsWith('/login/');
}

function isSharedPath(pathname: string) {
  return pathname.startsWith('/s/');
}

function parseWwwAuthenticateError(header: string | null): string | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (!/^bearer\b/i.test(trimmed)) return null;
  const errMatch = trimmed.match(/\berror\s*=\s*"([^"]+)"/i);
  return errMatch ? errMatch[1] : null;
}

function clearLocalAuthArtifacts() {
  try {
    localStorage.removeItem('jwt');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('app_jwt');
  } catch {
    // ignore
  }
}

export default function AuthDisconnectGate() {
  const location = useLocation();
  const { logout, user, loading } = useAuth();
  const suppressProbeUntilRef = React.useRef(0);

  const latestLocationRef = React.useRef({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  React.useEffect(() => {
    latestLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
  }, [location.hash, location.pathname, location.search]);

  // 1) React to auth-disconnect signals (from any HTTP call)
  React.useEffect(() => {
    return subscribeAuthDisconnected((evt) => {
      const cur = latestLocationRef.current.pathname;

      // Do not show the disconnect popup on the login page.
      // Login should show its own inline error state (wrong credentials / server down).
      if (isLoginPath(cur) || isSharedPath(cur)) {
        return;
      }

      const status = evt?.status;
      const isGatewayDown = status === 502 || status === 503 || status === 504;

      // Do not force-logout on transient connectivity issues.
      // Treat these as availability problems, not authentication problems.
      if (isGatewayDown || evt?.reason === 'unknown') {
        return;
      }

      let msg: AuthInlineMessage;
      if (evt?.reason === 'forbidden') {
        msg = {
          title: 'Access changed',
          description: 'Your access was updated. Please sign in again.',
        };
      } else if (evt?.reason === 'token-expired' || evt?.reason === 'unauthenticated') {
        msg = {
          title: 'Session ended',
          description: 'Your session expired. Please sign in again.',
        };
      } else {
        msg = {
          title: 'Disconnected',
          description: 'You were disconnected. Please sign in again.',
        };
      }

      // Best-effort clear (cookie logout may fail if already expired)
      clearLocalAuthArtifacts();
      void logout();
      postAuthEvent('logout');

      // Keep the user on the same URL, but persist an auth-required message.
      // This makes refresh safe: the login UI can rehydrate the message from sessionStorage.
      if (!isLoginPath(cur)) {
        const from = latestLocationRef.current;
        setAuthKickoutState({ message: msg, from, at: Date.now() });
      }
    });
  }, [logout]);

  // 2) Background health check to catch idle session expiry
  React.useEffect(() => {
    let timer: number | undefined;
    let stopped = false;

    const shouldSkipProbe = () => {
      if (loading) return true;
      if (!user) return true;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return true;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
      if (Date.now() < suppressProbeUntilRef.current) return true;
      return false;
    };

    async function tick() {
      if (stopped) return;
      if (shouldSkipProbe()) return;

      // Already disconnected — no point in polling; wait for re-login reset.
      if (isAuthDisconnected()) return;

      const path = latestLocationRef.current.pathname;
      if (isLoginPath(path) || isSharedPath(path)) return;

      try {
        const res = await fetch('/bff/auth/me', {
          method: 'GET',
          credentials: 'include',
          headers: { accept: 'application/json' },
        });

        // Disconnect only on explicit RFC 6750 invalid_token signal.
        if (
          res.status === 401 &&
          parseWwwAuthenticateError(res.headers.get('WWW-Authenticate')) === 'invalid_token'
        ) {
          notifyAuthDisconnected({
            reason: 'unauthenticated',
            status: res.status,
            url: '/bff/auth/me',
          });
        }
      } catch {
        // Transient network errors should not force-logout the user.
        // We'll retry on the next interval tick.
      }
    }

    timer = window.setInterval(() => { void tick(); }, 60_000);
    return () => {
      stopped = true;
      if (timer) window.clearInterval(timer);
    };
  }, [loading, user]);

  // After a successful login from any tab, suppress health probes briefly.
  // This prevents racey /auth/me 401s during session propagation from forcing a kickout.
  React.useEffect(() => {
    return subscribeAuthEvents((evt) => {
      if (evt.type !== 'login') return;
      suppressProbeUntilRef.current = Date.now() + 20_000;
    });
  }, []);

  return null;
}
